import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import AccountLoginPage from './pages/AccountLoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SiteLayout from './pages/site/SiteLayout';
import HomePage from './pages/site/HomePage';
import FeaturesPage from './pages/site/FeaturesPage';
import PricingPage from './pages/site/PricingPage';
import FaqPage from './pages/site/FaqPage';
import CommunityLayout from './pages/site/CommunityLayout';
import ForumPage from './pages/site/ForumPage';
import ThreadPage from './pages/site/ThreadPage';
import FeatureRequestsPage from './pages/site/FeatureRequestsPage';
import ReleasesPage from './pages/site/ReleasesPage';
import PlatformLayout from './pages/platform/PlatformLayout';
import PlatformConsole from './pages/platform/PlatformConsole';
import PlatformStaff from './pages/platform/PlatformStaff';
import SiteContent from './pages/platform/SiteContent';
import PlatformMarketing, { MarketingCampaignPage } from './pages/platform/PlatformMarketing';
import PlansAdmin from './pages/platform/PlansAdmin';
import PlanEditor from './pages/platform/PlanEditor';
import AccountOverview from './pages/platform/AccountOverview';
import PlatformAuditPage from './pages/platform/platformAudit';
import AdminLayout from './pages/admin/AdminLayout';
import FormBuilderAdmin from './pages/admin/FormBuilderAdmin';
import LogicAdmin from './pages/admin/LogicAdmin';
import SettingsLayout from './pages/admin/SettingsLayout';
import UsersAdmin from './pages/admin/UsersAdmin';
import GroupsAdmin from './pages/admin/GroupsAdmin';
import BillingSettings from './pages/admin/BillingSettings';
import PaymentsSettings from './pages/admin/PaymentsSettings';
import PlanPicker from './pages/admin/PlanPicker';
import AccountAccess from './pages/admin/AccountAccess';
import ProfileSettings from './pages/admin/ProfileSettings';
import AuditSettings from './pages/admin/AuditSettings';
import PrivacySettings from './pages/admin/PrivacySettings';
import ProviderEditor from './pages/admin/ProviderEditor';
import FormsList from './pages/admin/FormsList';
import FormWorkspace from './pages/admin/FormWorkspace';
import FormSettings from './pages/admin/FormSettings';
import FormVersionsPage from './pages/admin/FormVersions';
import FormDocument from './pages/admin/FormDocument';
import FormAnalytics from './pages/admin/FormAnalytics';
import FormSummary from './pages/admin/FormSummary';
import ApiSettings from './pages/admin/ApiSettings';
import IntegrationCredentials from './pages/admin/IntegrationCredentials';
import BrandingSettings from './pages/admin/BrandingSettings';
import PlatformSettings from './pages/platform/PlatformSettings';
import PlatformRules from './pages/platform/PlatformRules';
import WorkflowTab from './pages/admin/workflow/WorkflowTab';
import RunsPage from './pages/admin/workflow/RunsPage';
import FormAccessTab from './pages/admin/FormAccessTab';
import FormInvitations from './pages/admin/FormInvitations';
import SubmissionsList from './pages/admin/SubmissionsList';
import PublicFormPage from './pages/PublicFormPage';
import { ToastProvider } from './components/Toast';

/**
 * "Single sign-on" became "Account access" once the page grew to govern
 * password sign-in too. The old paths are in bookmarks and in links the app
 * itself has already handed out, so they keep resolving — provider sub-paths
 * included.
 */
function LegacySsoRedirect() {
  const rest = useParams()['*'];
  return <Navigate to={`/admin/settings/access${rest ? `/${rest}` : ''}`} replace />;
}

/**
 * One account's billing page became its account page's Billing tab (074). The
 * old path is in bookmarks, emails and the plan editor's history, so it keeps
 * resolving — onto that tab.
 */
function AccountBillingRedirect() {
  const { id } = useParams();
  return <Navigate to={`/platform/accounts/${id}?tab=billing`} replace />;
}

export default function App() {
  return (
    /*
     * Above the routes, not inside a layout: a save that navigates — creating
     * a provider, changing a plan — should still be able to say so on the
     * screen it lands on, and the corner it says it in is the same corner
     * everywhere in the app.
     */
    <ToastProvider>
      <Routes>
        {/* The public marketing site and community — the front door strangers
            arrive at, with self-serve signup one click away. */}
        <Route element={<SiteLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FaqPage />} />
          {/* The Jotform comparison is gone (Ben, 2026-09-27): the product
              stands on its own. An old /compare/jotform link falls through
              to the catch-all below and lands on the home page. */}
          <Route path="/community" element={<CommunityLayout />}>
            <Route index element={<ForumPage />} />
            <Route path="threads/:id" element={<ThreadPage />} />
            <Route path="feature-requests" element={<FeatureRequestsPage />} />
            <Route path="releases" element={<ReleasesPage />} />
          </Route>
        </Route>

        <Route path="/login" element={<LoginPage />} />
        {/* One account's own sign-in page — a link IT can publish, and the way
            past a domain two accounts both claim. */}
        <Route path="/login/:accountSlug" element={<AccountLoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        {/* Forgotten passwords, and invitations. Outside every layout: whoever
            needs these has no session, and these are the addresses printed in
            the emails. One component serves both — the token says which it is,
            and the page reads differently for somebody arriving for the first
            time than for somebody who has forgotten their password. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<ResetPasswordPage />} />

        {/* Public forms. The canonical link carries the account; the single-segment
            form still resolves for links made before accounts existed. */}
        <Route path="/f/:accountSlug/:slug" element={<PublicFormPage />} />
        <Route path="/f/:slug" element={<PublicFormPage />} />

        {/* Service owner console, with its own grouped sidebar (R12). */}
        <Route path="/platform" element={<PlatformLayout />}>
          <Route index element={<PlatformConsole />} />
          <Route path="plans" element={<PlansAdmin />} />
          {/* Two routes, one component: "new" carries no :id, which is exactly
              how the editor knows it is creating rather than loading. */}
          <Route path="plans/new" element={<PlanEditor />} />
          <Route path="plans/:id" element={<PlanEditor />} />
          {/* One account, whole (074): Overview · Users · Billing · Activity in ?tab=. */}
          <Route path="accounts/:id" element={<AccountOverview />} />
          {/* The billing page it replaced keeps resolving, onto its tab — bookmarks and old links land. */}
          <Route path="accounts/:id/billing" element={<AccountBillingRedirect />} />
          <Route path="staff" element={<PlatformStaff />} />
          {/* Prospecting email: campaigns, lists, and the send throttle. */}
          <Route path="marketing" element={<PlatformMarketing />} />
          <Route path="marketing/:id" element={<MarketingCampaignPage />} />
          {/* What the public site says: releases and FAQ. */}
          <Route path="content" element={<SiteContent />} />
          {/* Stripe, email and upload storage — everything configurable without
              a redeploy. Storage kept its own path before Settings existed. */}
          <Route path="settings" element={<PlatformSettings />} />
          {/* The numbers the service runs on: every rung reads, a Super Admin edits. */}
          <Route path="rules" element={<PlatformRules />} />
          <Route path="storage" element={<Navigate to="/platform/settings?tab=storage" replace />} />
          {/* R12: the whole platform trail, by area, with the masquerade log merged in. */}
          <Route path="audit" element={<PlatformAuditPage />} />
          <Route path="*" element={<Navigate to="/platform" replace />} />
        </Route>

        {/* One customer account */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<FormsList />} />
          <Route path="forms/:formId" element={<FormWorkspace />}>
            <Route index element={<Navigate to="build" replace />} />
            <Route path="build" element={<FormBuilderAdmin />} />
            {/* The widget containers live on the builder canvas now; the old
                tab's links and bookmarks land there. */}
            <Route path="widgets" element={<Navigate to="../build" replace />} />
            <Route path="logic" element={<LogicAdmin />} />
            {/* The form's PDF, out of the fifth card on Settings and into the
                place where "make it" lives — between Logic and Workflow. */}
            <Route path="document" element={<FormDocument />} />
            <Route path="access" element={<FormAccessTab />} />
            <Route path="workflow" element={<WorkflowTab />} />
            {/* Automate › Runs (R6): what happened to each submission. */}
            <Route path="runs" element={<RunsPage />} />
            {/* Follow-up folded into the Workflow canvas (F4). The link
                survives as a redirect so bookmarks and the docs still land. */}
            <Route path="followup" element={<Navigate to="../workflow" replace />} />
            {/* Email a list a private link each — surveys' distribution arm. */}
            <Route path="invitations" element={<FormInvitations />} />
            {/* The drawer opens over the table, so both routes render the list. */}
            <Route path="submissions" element={<SubmissionsList />} />
            <Route path="submissions/:submissionId" element={<SubmissionsList />} />
            <Route path="summary" element={<FormSummary />} />
            <Route path="analytics" element={<FormAnalytics />} />
            <Route path="settings" element={<FormSettings />} />
            {/* Settings › Versions (R3): the history that was a modal, as a page. */}
            <Route path="versions" element={<FormVersionsPage />} />
          </Route>

          {/* Settings, with its own sub-tab row. */}
          <Route path="settings" element={<SettingsLayout />}>
            {/* The first page everyone can see (R10): the You group leads the sidebar. */}
            <Route index element={<Navigate to="/admin/settings/profile" replace />} />
            <Route path="profile" element={<ProfileSettings />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="groups" element={<GroupsAdmin />} />
            <Route path="access" element={<AccountAccess />} />
            {/* Security became Profile & security (R10, §2); the old path keeps working. */}
            <Route path="security" element={<Navigate to="/admin/settings/profile" replace />} />
            <Route path="billing" element={<BillingSettings />} />
            {/* The plan picker sits in the same frame, Billing & plan lit beside it (R11). */}
            <Route path="billing/plans" element={<PlanPicker />} />
            <Route path="payments" element={<PaymentsSettings />} />
            <Route path="api" element={<ApiSettings />} />
            <Route path="integrations" element={<IntegrationCredentials />} />
            <Route path="branding" element={<BrandingSettings />} />
            <Route path="audit" element={<AuditSettings />} />
            <Route path="privacy" element={<PrivacySettings />} />
            {/* One provider fills the content column; the sidebar keeps
                Sign-in & access lit beside it (R10). */}
            <Route path="access/providers/:id" element={<ProviderEditor />} />
          </Route>

          {/* Where Users and Single sign-on used to live. */}
          <Route path="users" element={<Navigate to="/admin/settings/users" replace />} />
          <Route path="settings/sso/*" element={<LegacySsoRedirect />} />

          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>

        {/* Unknown paths land on the public site, not the login wall. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ToastProvider>
  );
}
